import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Loader from "src/app/polar-SAAS-Components/components/Loader";
import { getKits, getKitsByProject, getProjectsForUser } from "src/services/corporateService";
import shippingTruckIcon from "../../../../../assets/images/icons/shipping-truck.png";
import wifiDisabledIcon from "../../../../../assets/images/icons/wifi-disabled.png";
import alertTriangleIcon from "../../../../../assets/images/icons/alert-triangle.png";
import tempHotIcon from "../../../../../assets/images/icons/temp-hot-fill.png";
import { SensorIcon } from "src/app/polar-SAAS-Components/components/Icons";
import { openFullScreen } from "src/app/utils";
// Fullscreen is triggered from the caller (e.g., button click) and re-requested on mount.

type EntityStatus = "Active" | "Warning" | "Offline" | "Inactive";

type EntityCardData = {
  id: string;
  name: string;
  description?: string;
  status: EntityStatus;
  primaryLabel: string;
  primaryValue: string;
  lastSeen?: string;
  corporateId?: string;
  projectId?: string;
  isInactive?: boolean;
};

const PAGE_SIZE = 8;

const normalizeStatus = (status?: string): EntityStatus => {
  const value = (status || "").toString().toLowerCase();
  if (value.includes("warn")) return "Warning";
  if (value.includes("off")) return "Offline";
  return "Active";
};

const formatDate = (value?: string) => {
  if (!value) return "Not updated yet";
  const parsed = new Date(value);
  return isNaN(parsed.getTime())
    ? value
    : `${parsed.toLocaleDateString()} ${parsed.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
};

type EntityViewProps = {
  projectId?: string;
};

const EntityView: React.FC<EntityViewProps> = ({ projectId }) => {
  const [entities, setEntities] = useState<EntityCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [projectName, setProjectName] = useState("");
  const [pageStart, setPageStart] = useState(0);
  const history = useHistory();
  const location = useLocation();
  const queryProjectId = new URLSearchParams(location.search).get("projectId") || undefined;
  const effectiveProjectId = projectId || queryProjectId;
  const statusIcon = (status: EntityStatus) => {
    if (status === "Warning") return alertTriangleIcon;
    if (status === "Offline") return wifiDisabledIcon;
    return tempHotIcon;
  };

  // Try to enter fullscreen on mount (after navigation).
  useEffect(() => {
    openFullScreen().catch(() => {});
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const responses = await Promise.all([
          effectiveProjectId ? getKitsByProject(effectiveProjectId) : getKits(),
          getProjectsForUser()
        ]);
        const kitRes = responses[0];
        const projectRes = responses[1];

        const kitData = kitRes && kitRes.data
          ? (kitRes.data.content ? kitRes.data.content : kitRes.data)
          : [];
        const projectData = projectRes && (projectRes as any).data
          ? ((projectRes as any).data.content ? (projectRes as any).data.content : (projectRes as any).data)
          : [];

        const kitToCorporateMap: Record<string, string> = {};
        const kitToProjectMap: Record<string, string> = {};

        projectData.forEach((project: any) => {
          const corporateId = project.corporateId;
          const projectId = project.id;
          const kitsInProject: string[] = project.kits || [];
          kitsInProject.forEach((kitId: string) => {
            kitToCorporateMap[kitId] = corporateId;
            kitToProjectMap[kitId] = projectId;
          });
        });

        if (effectiveProjectId) {
          const match = projectData.find((p: any) => p.id === effectiveProjectId);
          if (match && match.name) {
            setProjectName(match.name);
          } else {
            setProjectName("");
          }
        }

        const mapped: EntityCardData[] = kitData.map((kit: any, index: number): EntityCardData => {
          const properties = Array.isArray(kit.properties) ? kit.properties : [];
          const primaryProp =
            properties.find((prop: any) => prop.code === "T") || properties[0] || null;
          const normalizedStatus = normalizeStatus(
            kit.status || kit.state || kit.connectionStatus || kit.deviceStatus
          );
          const entityId = kit.id || kit.kitId || kit.name || `entity-${index}`;
          const mappedProjectId = kitToProjectMap[kit.id] || kit.projectId || (kit.project && kit.project.id);
          const hasOfflineFlag = kit.offline !== undefined;

          let status: EntityStatus = normalizedStatus;
          const rawStatus = (kit.status || "").toString().toLowerCase();
          const isInactive = rawStatus === "inactive";

          if (isInactive) {
            status = "Inactive";
          } else if (hasOfflineFlag) {
            status = kit.offline ? "Offline" : "Active";
          }

          return {
            id: entityId,
            name: kit.name || "Unnamed Entity",
            description: kit.description || (kit.metaData && kit.metaData.item) || "",
            status,
            primaryLabel:
              (primaryProp && primaryProp.displayName) ? primaryProp.displayName : "Reading",
            primaryValue:
              (primaryProp && primaryProp.displayValue) ? primaryProp.displayValue : "--",
            lastSeen: kit.lastSeen || kit.lastUpdate || kit.updatedAt || "",
            corporateId: kitToCorporateMap[kit.id],
            projectId: mappedProjectId,
            isInactive,
          };
        });

        const filtered = effectiveProjectId ? mapped.filter((m) => m.projectId === effectiveProjectId) : mapped;
        console.log("Loaded entities:", filtered); 
        setEntities(filtered);
      } catch (err) {
        console.error("Failed to load entities", err);
        setError("Unable to load entities right now. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [effectiveProjectId]);

  useEffect(() => {
    setPageStart(0);
    if (entities.length <= PAGE_SIZE) return;
    const id = setInterval(() => {
      setPageStart((prev) => {
        const next = prev + PAGE_SIZE;
        return next >= entities.length ? 0 : next;
      });
    }, 5000);
    return () => clearInterval(id);
  }, [entities]);

  const visibleEntities = useMemo(() => {
    if (entities.length <= PAGE_SIZE) return entities;
    const slice = entities.slice(pageStart, pageStart + PAGE_SIZE);
    if (slice.length < PAGE_SIZE) {
      return slice.concat(entities.slice(0, PAGE_SIZE - slice.length));
    }
    return slice;
  }, [entities, pageStart]);

  const summary = useMemo(() => {
    return entities.reduce(
      (acc, entity) => {
        if (entity.status === "Warning") acc.warning += 1;
        else if (entity.status === "Offline") acc.offline += 1;
        else acc.active += 1;
        acc.total += 1;
        return acc;
      },
      { total: 0, active: 0, warning: 0, offline: 0 }
    );
  }, [entities]);

  const now = useMemo(() => new Date(), []);

  return (
    <div className="entity-view entity-view--dark">
      <div className="entity-view__shell">
        <div className="entity-view__header">
          <div className="entity-view__header-left">
            <button
              className="entity-view__back"
              onClick={() => history.length > 1 && history.goBack()}
              aria-label="Go back"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#A2B6E1" strokeWidth="2">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div className="entity-view__title-block">
              <div className="entity-view__title-icon">
                <img src={shippingTruckIcon} alt="" width={18} height={18} />
              </div>
              <div>
                <h2 className="entity-view__title">
                  {projectName ? `${projectName}` : ""}
                </h2>
                <p className="entity-view__subtitle">
                  {entities.length ? (summary.active > 0 ? "Active" : "Inactive") : "Project status unavailable"}
                </p>
              </div>
            </div>
          </div>
          <div className="entity-view__time">
            <span className="entity-view__clock">
              {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })}
            </span>
            <span className="entity-view__date">
              {now.toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
          </div>

        <div className="entity-view__summary entity-view__summary--dark">
          {[
            { label: "Total", value: summary.total, tone: "neutral", icon: shippingTruckIcon },
            { label: "Active", value: summary.active, tone: "success", icon: tempHotIcon },
            { label: "Warning", value: summary.warning, tone: "warning", icon: alertTriangleIcon },
            { label: "Offline", value: summary.offline, tone: "offline", icon: wifiDisabledIcon },
          ].map((item) => (
            <div
              key={item.label}
              className={`entity-view__summary-card entity-view__summary-card--${item.tone}`}
            >
              <div className="entity-view__summary-icon entity-view__summary-icon--dark">
                <img src={item.icon} alt="" width={18} height={18} />
              </div>
              <div className="entity-view__summary-text">
                <p className="entity-view__summary-label">{item.label}</p>
                <p className="entity-view__summary-value">{item.value}</p>
              </div>
            </div>
          ))}
        </div>

        {error && <div className="entity-view__error">{error}</div>}

        <div className="entity-view__grid entity-view__grid--dark">
          {visibleEntities.map((entity) => (
            <div
              key={entity.id}
              className={`entity-card entity-card--dark entity-card--${entity.status.toLowerCase()} ${
                entity.isInactive ? "entity-card--blur" : ""
              }`}
              role="article"
            >
              <div className="entity-card__top">
                <div className={`entity-card__icon entity-card__icon--${entity.status.toLowerCase()}`}>
                  <img src={statusIcon(entity.status)} alt="" width={18} height={18} />
                </div>
                <span className={`entity-card__badge entity-card__badge--${entity.status.toLowerCase()}`}>
                  {entity.status}
                </span>
              </div>

              <div className="entity-card__title entity-card__title--center">
                <h3>{entity.name}</h3>
                <p>{entity.description || "Keels DC Reefer Trucks"}</p>
              </div>

              <div className="entity-card__metric">
                <span className="entity-card__metric-value">{entity.primaryValue}</span>
              </div>

              <div className="entity-card__footer">
                <span className="entity-card__footer-label">Last Updated</span>
                <span className="entity-card__footer-value">{formatDate(entity.lastSeen)}</span>
              </div>
            </div>
          ))}

          {!loading && !entities.length && (
            <div className="entity-view__empty">
              <SensorIcon />
              <p>No entities found.</p>
            </div>
          )}
        </div>
      </div>

      <Loader isLoading={loading} fullScreen={false} />
    </div>
  );
};

export default EntityView;
